import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { supabase } from '../lib/supabase';

interface User {
  id: string;
  email: string;
  name: string;
  role: 'Nurse' | 'Physician' | 'Pharmacist' | 'Admin' | 'Student';
  organization?: string;
  country?: string;
  isVerified: boolean;
  subscription: 'free' | 'professional' | 'enterprise';
  createdAt: string;
}

interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<{ success: boolean; error?: string }>;
  register: (data: RegisterData) => Promise<{ success: boolean; error?: string }>;
  logout: () => Promise<void>;
  updateUser: (data: Partial<User>) => Promise<boolean>;
  resetPassword: (email: string) => Promise<{ success: boolean; error?: string }>;
}

interface RegisterData {
  email: string;
  password: string;
  name: string;
  role: User['role'];
  organization?: string;
  country?: string;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const profileToUser = (profile: any): User => ({
    id: profile.id,
    email: profile.email,
    name: profile.name || 'User',
    role: profile.role || 'Student',
    organization: profile.organization,
    country: profile.country,
    isVerified: profile.is_verified || false,
    subscription: profile.subscription || 'free',
    createdAt: profile.created_at || new Date().toISOString(),
  });

  useEffect(() => {
    const initializeAuth = async () => {
      try {
        const { data } = await supabase.auth.getSession();
        if (data.session?.user) {
          const { data: profile } = await supabase
            .from('profiles')
            .select('*')
            .eq('id', data.session.user.id)
            .single();
          if (profile) {
            setUser(profileToUser(profile));
          }
        }
      } catch (error) {
        console.error('Auth init error:', error);
      } finally {
        setIsLoading(false);
      }
    };

    initializeAuth();

    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event) => {
      if (event === 'SIGNED_OUT') {
        setUser(null);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const login = async (email: string, password: string): Promise<{ success: boolean; error?: string }> => {
    try {
      setIsLoading(true);
      const { data, error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) {
        setIsLoading(false);
        return { success: false, error: error.message };
      }
      if (data.user) {
        await new Promise(resolve => setTimeout(resolve, 100));
        const { data: profile } = await supabase
          .from('profiles')
          .select('*')
          .eq('id', data.user.id)
          .single();
        if (profile) {
          setUser(profileToUser(profile));
        }
        setIsLoading(false);
        return { success: true };
      }
      setIsLoading(false);
      return { success: false, error: 'Login failed' };
    } catch (err: any) {
      setIsLoading(false);
      return { success: false, error: err.message || 'An error occurred' };
    }
  };

  const register = async (registerData: RegisterData): Promise<{ success: boolean; error?: string }> => {
    try {
      setIsLoading(true);
      const { error: authError } = await supabase.auth.signUp({
        email: registerData.email,
        password: registerData.password,
        options: {
          data: { 
            name: registerData.name, 
            role: registerData.role, 
            organization: registerData.organization, 
            country: registerData.country 
          },
        },
      });
      setIsLoading(false);
      if (authError) return { success: false, error: authError.message };
      return { success: true };
    } catch (err: any) {
      setIsLoading(false);
      return { success: false, error: err.message || 'An error occurred' };
    }
  };

  const logout = async () => {
    setUser(null);
    await supabase.auth.signOut();
    window.location.href = '/';
  };

  const updateUser = async (updateData: Partial<User>): Promise<boolean> => {
    if (!user) return false;
    try {
      const { error } = await supabase
        .from('profiles')
        .update({ 
          name: updateData.name, 
          role: updateData.role, 
          organization: updateData.organization, 
          country: updateData.country 
        })
        .eq('id', user.id);
      if (error) return false;
      setUser({ ...user, ...updateData });
      return true;
    } catch {
      return false;
    }
  };

  const resetPassword = async (email: string): Promise<{ success: boolean; error?: string }> => {
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/auth/reset-password`,
      });
      if (error) return { success: false, error: error.message };
      return { success: true };
    } catch (err: any) {
      return { success: false, error: err.message || 'An error occurred' };
    }
  };

  return (
    <AuthContext.Provider value={{ user, isLoading, login, register, logout, updateUser, resetPassword }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
